import { Box, InlineFormattedNumber, setAlpha, Text } from '@talos/kyoko';
import type { ICellRendererParams } from 'ag-grid-community';
import { useTheme } from 'styled-components';

export interface ColorBarProps {
  value: number | null;
  /** null when there is no data to scale against; bars render at 0 width */
  maxValue: number | null;
  color: string;
  unit?: string;
  unitColor?: string;
  height?: string;
  showValue?: boolean;
  /** InlineFormattedNumber increment controlling displayed decimal places. Default: "0.01" */
  increment?: string;
}

function normalizeColorBarValue(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Reusable color bar component that displays a value as a percentage bar.
 * Can be used standalone or as an AG Grid cell renderer.
 * Shows "-" when value is null (missing data).
 * Uses InlineFormattedNumber for hover-to-expand full precision.
 */
export function ColorBar({
  value,
  maxValue,
  color,
  unit,
  unitColor,
  height = '24px',
  showValue = true,
  increment = '0.01',
}: ColorBarProps) {
  const theme = useTheme();
  const isNullValue = value === null;
  const defaultUnitColor = setAlpha(0.52, theme.colors.gray['100']);
  const hasValidScale = !isNullValue && maxValue != null && maxValue > 0;
  const barWidth = hasValidScale ? Math.min((value / maxValue) * 100, 100) : 0;

  return (
    <Box position="relative" w="100%" h={height} borderRadius="borderRadiusSmall">
      {!isNullValue && (
        <Box
          data-testid="color-bar-fill"
          position="absolute"
          top="0"
          right="0"
          h="100%"
          w={`${barWidth}%`}
          background={color}
          borderRadius="borderRadiusSmall"
        />
      )}
      {showValue && (
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          gap="spacing4"
          pr="spacing8"
        >
          {isNullValue ? (
            <Text size="fontSizeSmall" color="colorTextImportant">
              -
            </Text>
          ) : (
            <InlineFormattedNumber number={value} increment={increment} align="right" />
          )}
          {unit && !isNullValue && (
            <Text size="fontSizeSmall" color={unitColor ?? defaultUnitColor}>
              {unit}
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
}

export interface CreateColorBarCellRendererProps<TData> {
  field: keyof TData;
  color: string;
  maxValue: number | null;
  unit?: string;
  unitColor?: string;
  height?: string;
  increment?: string;
  /** Transforms the raw data value before rendering. Useful for e.g. converting ratio to percentage. */
  transformValue?: (value: number) => number;
}

/**
 * Factory function to create an AG Grid cell renderer that uses the ColorBar component.
 * The renderer expects the cell data to have a numeric value at the specified field.
 */
export function createColorBarCellRenderer<TData>(props: CreateColorBarCellRendererProps<TData>) {
  const { field, color, maxValue, unit, unitColor, height, increment, transformValue } = props;
  return ({ data }: ICellRendererParams<TData>) => {
    if (!data) {
      return null;
    }
    const rawValue = normalizeColorBarValue(data[field]);
    const value = rawValue != null && transformValue ? transformValue(rawValue) : rawValue;
    const scaledMaxValue = maxValue != null && transformValue ? transformValue(maxValue) : maxValue;

    return (
      <ColorBar
        value={value}
        maxValue={scaledMaxValue}
        color={color}
        unit={unit}
        unitColor={unitColor}
        height={height}
        increment={increment}
      />
    );
  };
}
